import axios, { AxiosError } from "axios";

/**
 * Extracts the error message from an SDK error object.
 *
 * @param e The error object, which may contain a response body with the error message.
 * @returns A string containing the error message, or `undefined` if no message is found.
 */
const getErrorMessageFromSDK = async (e: any): Promise<string | undefined> => {
  let error;
  try {
    const data = (await e?.response?.body?.getReader().read())?.value;
    if (data) {
      const str = String.fromCharCode(...data);
      error = JSON.parse(str);
    }
  } catch (e) {
    console.log("Unexpected Error", e);
  }

  return error?.message;
};

const getErrorMessageFromClient = (e: unknown): string | undefined => {
  const error = e as AxiosError;

  if (axios.isAxiosError(error)) {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.errorMessage ||
      (typeof error?.response?.data?.error === "string"
        ? error?.response?.data?.error
        : undefined)
    );
  }

  return undefined;
};

/**
 * This catch error is to be used from the APIs created on @practice/sdk
 *
 * @deprecated Use `getErrorMessage` instead.
 * */
export const catchErrorSDK = async (e: any, fallBackMessage: string) => {
  return (await getErrorMessageFromSDK(e)) || fallBackMessage;
};

/**
 * This catch error is to be used from the APIs created on @practice/web
 *
 * @deprecated Use `getErrorMessage` instead.
 * */
export const catchErrorClient = (e: unknown, fallBackMessage: string) => {
  return getErrorMessageFromClient(e) || fallBackMessage;
};

/**
 * Returns the error message from the error object from the APIs created on @practice/web and @practice/sdk
 *
 * @param error The error object
 * @param fallBackMessage The fallback message to return if the error object does not contain a message
 * @returns The error message
 */
export const getErrorMessage = async (
  error: unknown,
  fallBackMessage: string
): Promise<string> => {
  let message = await getErrorMessageFromSDK(error);
  if (!message) {
    message = getErrorMessageFromClient(error);
  }
  return message || fallBackMessage;
};

export const getErrorCode = (e: unknown) => {
  const error = e as AxiosError;

  return error?.response?.data?.error?.code;
};
